<template>
  <maplibre-map ref="map" :bounds="boundariesFormatted" :max-zoom="15" :show-geolocate-control="true"
                @loaded="onMapLoaded">
    <template #default="{ map }">
      <component :is="activePointComponent" v-if="activePoints" :key="activePointKeys[0]"
                 ref="marker" :map="map" :points="activePoints" @close="resetActivePoints" />
    </template>
  </maplibre-map>
</template>

<script>
import { mapGetters } from 'vuex'
import MaplibreMap from '@/components/maplibre-map'

const MAP_LAYER_NAME_POINTS = 'points'

export default {
  components: { MaplibreMap },

  props: {
    activePointComponent: {
      type: Object,
      required: true
    },
    points: {
      type: Array,
      required: true
    },
    boundaries: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      accentColor: null,
      activePointKeys: null,
      boundariesAwaitingApplication: false,
      mapLoaded: false,
      secondaryAccentColor: null
    }
  },

  computed: {
    ...mapGetters(['geoJsonPoints']),
    boundariesFormatted() {
      const {minLatitude, maxLatitude, minLongitude, maxLongitude } = this.boundaries
      return [[minLongitude, minLatitude], [maxLongitude, maxLatitude]]
    },
    activePoints() {
      if (this.activePointKeys) {
        return this.activePointKeys.map(p =>
          this.points.find(q => q.key === p))
      }
      return null
    }
  },

  watch: {
    boundaries() {
      if (this.mapLoaded) {
        this.applyBoundsToMap()
      } else {
        this.boundariesAwaitingApplication = true
      }
    },
    geoJsonPoints() {
      if (this.mapGl) {
        this.applyPointsToMap()
      }
    }
  },

  created() {
    const documentComputedStyle = getComputedStyle(document.documentElement)
    this.accentColor = documentComputedStyle.getPropertyValue('--accent-color')
    this.secondaryAccentColor = documentComputedStyle.getPropertyValue('--secondary-accent-color')
  },

  methods: {
    async applyBoundsToMap() {
      this.mapGl.fitBounds(this.boundariesFormatted, {padding: 30})
    },

    // updates the point data without requiring the entire layer to be re-initialized.
    applyPointsToMap() {
      this.mapGl.getSource(MAP_LAYER_NAME_POINTS).setData(this.geoJsonPoints)
    },

    initializeMapPointsLayer() {
      this.mapGl.addSource(MAP_LAYER_NAME_POINTS, {type: 'geojson', data: this.geoJsonPoints})
      // Add a layer showing the places.
      this.mapGl.addLayer({
        id: MAP_LAYER_NAME_POINTS,
        type: 'circle',
        source: MAP_LAYER_NAME_POINTS,
        paint: {
          'circle-radius': 5,
          'circle-color': [
            'match',
            ['get', 'type'],
            'Event',
            this.accentColor,
            'ExternalEvent',
            this.accentColor,
            'Group',
            this.secondaryAccentColor,
            this.accentColor // Fallback, which is required
          ],
          'circle-stroke-color': 'white',
          'circle-stroke-width': 2,
          'circle-opacity': 1
        }
      })

      this.mapGl.on('click', MAP_LAYER_NAME_POINTS, e => {
        this.activePointKeys = e.features.map(p => p.properties.key)
      })
      this.mapGl.on('mousemove', MAP_LAYER_NAME_POINTS, () => {
        this.mapGl.getCanvas().style.cursor = 'pointer'
      })
      this.mapGl.on('mouseleave', MAP_LAYER_NAME_POINTS, () => {
        this.mapGl.getCanvas().style.cursor = ''
      })
    },

    resetActivePoints() {
      this.activePointKeys = null
    },

    onMapLoaded({map}) {
      this.mapLoaded = true
      this.mapGl = map

      if (this.boundariesAwaitingApplication) {
        this.applyBoundsToMap()
      }

      this.initializeMapPointsLayer()
    }
  }
}
</script>
